import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Users, Mail, Shield, X, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };
const ROLE_LABELS = { admin: 'Admin', hr: 'HR Manager', employee: 'Employee', public_user: 'Public Viewer' };
const ROLE_DESCRIPTIONS = {
  admin: 'Full access including settings & billing',
  hr: 'Manage employees & PAFs',
  employee: 'View only their own information',
  public_user: 'Read-only access to all info',
};

export default function TeamPage() {
  const { apiCall, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'hr', first_name: '', last_name: '' });
  const [inviting, setInviting] = useState(false);

  const fetchTeam = async () => {
    try {
      const res = await apiCall('get', '/team/members');
      setMembers(res.data.members || []);
      setInvites(res.data.invites || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchTeam(); }, []);

  const handleInvite = async () => {
    if (!inviteForm.email) { toast.error('Email is required'); return; }
    // Frontend pre-check: same domain as logged-in admin
    const adminEmail = (user?.email || '').toLowerCase();
    const inviteeEmail = inviteForm.email.trim().toLowerCase();
    if (adminEmail.includes('@') && inviteeEmail.includes('@')) {
      const adminDomain = adminEmail.split('@')[1];
      const inviteeDomain = inviteeEmail.split('@')[1];
      if (adminDomain !== inviteeDomain) {
        toast.error(`Invitee email must be on the same domain as your account (@${adminDomain})`);
        return;
      }
    }
    setInviting(true);
    try {
      await apiCall('post', '/team/invite', inviteForm);
      toast.success(`Invite sent to ${inviteForm.email}`);
      setShowInviteDialog(false);
      setInviteForm({ email: '', role: 'hr', first_name: '', last_name: '' });
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally { setInviting(false); }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await apiCall('put', `/team/members/${memberId}`, { role: newRole });
      toast.success('Role updated');
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from your team?`)) return;
    try {
      await apiCall('delete', `/team/members/${memberId}`);
      toast.success('Member removed');
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await apiCall('delete', `/team/invites/${inviteId}`);
      toast.success('Invite cancelled');
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <div data-testid="team-page">
      <motion.div {...fadeIn} transition={{ duration: 0.3 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Team</h1>
            <p className="text-[#64748B]">Manage users, roles, and invitations</p>
          </div>
          <Button
            data-testid="invite-member-btn"
            onClick={() => setShowInviteDialog(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 font-semibold shadow-md hover:shadow-lg hover:shadow-indigo-500/20 transition-all active:scale-[0.98]"
          >
            <Mail className="w-4 h-4 mr-2" /> Invite Member
          </Button>
        </div>
      </motion.div>

      {/* Role Legend */}
      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.05 }} className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {Object.entries(ROLE_LABELS).map(([key, label]) => (
          <div key={key} className="bg-white rounded-xl border border-indigo-100/50 p-4 shadow-sm">
            <p className="text-sm font-bold text-[#0F172A] mb-0.5">{label}</p>
            <p className="text-xs text-[#94A3B8]">{ROLE_DESCRIPTIONS[key]}</p>
          </div>
        ))}
      </motion.div>

      {/* Members */}
      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.1 }} className="bg-white rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-indigo-50">
          <h2 className="text-base font-bold text-[#0F172A]">Team Members ({members.length})</h2>
        </div>
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin mx-auto" /></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {members.map(member => (
              <div key={member.user_id} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50/50 transition-colors" data-testid={`team-member-${member.user_id}`}>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm">
                  {member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{member.name || 'Unnamed'}</p>
                  <p className="text-xs text-[#94A3B8] truncate">{member.email}</p>
                </div>
                <div className="text-xs text-[#64748B]">{member.created_at ? new Date(member.created_at).toLocaleDateString() : ''}</div>
                {member.user_id === user?.user_id ? (
                  <span className="px-3 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full flex items-center gap-1.5 flex-shrink-0">
                    <Shield className="w-3 h-3" /> Owner
                  </span>
                ) : (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Select value={member.role || 'hr'} onValueChange={v => handleChangeRole(member.user_id, v)}>
                      <SelectTrigger className="w-36 h-9 rounded-lg border-slate-200 text-xs" data-testid={`role-select-${member.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="hr">HR Manager</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="public_user">Public Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => handleRemoveMember(member.user_id, member.email)}
                      className="p-2 rounded-lg hover:bg-red-50 text-[#94A3B8] hover:text-red-500 transition-colors"
                      data-testid={`remove-member-${member.user_id}`}
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.15 }} className="mt-6 bg-white rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-indigo-50">
            <h2 className="text-base font-bold text-[#0F172A]">Pending Invitations ({invites.length})</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {invites.map(invite => (
              <div key={invite.id} className="flex items-center gap-4 px-6 py-4" data-testid={`pending-invite-${invite.id}`}>
                <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{invite.email}</p>
                  <p className="text-xs text-[#94A3B8]">Invited as <span className="capitalize font-medium text-[#64748B]">{ROLE_LABELS[invite.role] || invite.role}</span></p>
                </div>
                <span className="px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-200">Pending</span>
                <button
                  onClick={() => handleCancelInvite(invite.id)}
                  className="text-xs text-red-500 hover:text-red-600 font-medium hover:underline"
                  data-testid={`cancel-invite-${invite.id}`}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A] tracking-tight">Invite Team Member</DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">Send an email invitation to join your organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Email *</Label>
              <Input data-testid="invite-email-input" type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} placeholder={user?.email ? `colleague@${user.email.split('@')[1] || 'company.com'}` : 'colleague@company.com'} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
              {user?.email?.includes('@') && (
                <p className="text-xs text-[#64748B] mt-1.5">
                  Must be on the <span className="font-semibold text-[#4F46E5]">@{user.email.split('@')[1]}</span> domain
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">First Name</Label>
                <Input data-testid="invite-first-name" value={inviteForm.first_name} onChange={e => setInviteForm({...inviteForm, first_name: e.target.value})} placeholder="Jane" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Last Name</Label>
                <Input data-testid="invite-last-name" value={inviteForm.last_name} onChange={e => setInviteForm({...inviteForm, last_name: e.target.value})} placeholder="Smith" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Role *</Label>
              <Select value={inviteForm.role} onValueChange={v => setInviteForm({...inviteForm, role: v})}>
                <SelectTrigger className="rounded-xl border-[#E2E8F0] h-11 bg-white shadow-sm" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — Full access including settings & billing</SelectItem>
                  <SelectItem value="hr">HR Manager — Manage employees & PAFs</SelectItem>
                  <SelectItem value="employee">Employee — View only their own information</SelectItem>
                  <SelectItem value="public_user">Public Viewer — Read-only access to all info</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowInviteDialog(false)} className="rounded-xl px-6 border-[#E2E8F0]">Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting || !inviteForm.email}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 shadow-md hover:shadow-lg hover:shadow-indigo-500/20" data-testid="send-invite-btn"
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
              {inviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
